import { forwardRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { noop } from '@toolbox/utils';
import { InputError } from '../Input/InputError';
import { EmptyCircleIcon } from '../Icon/icons/EmptyCircleIcon';
import { CheckedCircleIcon } from '../Icon/icons/CheckedCircleIcon';

import type { InputHTMLAttributes, ReactChild } from 'react';
import type { Modify } from '@toolbox/utils';

const Option = styled.span<{ selected: boolean; variant?: SwitchVariant }>`
  flex: 0.5;
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 16px;
  height: 100%;
  border: ${(props) =>
    props.variant === 'mini' ? '' : props.selected ? ' 1px solid #004B54' : '1px solid #9d9d9d'};
  padding: ${(props) => (props.variant === 'mini' ? '' : '16px 24px')};
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    ${(props) => (props.variant === 'mini' ? '' : 'border: 1px solid #004b54')};
  }

  input {
    border: 0;
    clip: rect(0px, 0px, 0px, 0px);
    height: 1px;
    width: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    white-space: nowrap;
    position: absolute;
  }
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const Container = styled.div<{ width?: string }>`
  display: flex;
  column-gap: 16px;
  justify-content: space-between;
  align-items: center;
  width: ${(props) => props.width};
`;

export type SwitchVariant = 'mini' | 'default';

export type SwitchProps<T extends string | number = string | number> = Modify<
  InputHTMLAttributes<HTMLInputElement>,
  {
    options: string[];
    width: string;
    value: T;
    error?: ReactChild | boolean | null;
    defaultValue?: T;
    variant?: SwitchVariant;
    onChange?: (value: T) => void;
    keepErrorSpace?: boolean;
  }
>;

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(props, ref) {
  const {
    defaultValue,
    options,
    width,
    onChange: onChangeProp,
    name,
    error,
    variant,
    keepErrorSpace,
    ...switchProps
  } = props;
  const [selectedOption, setSelectedOption] = useState<string>(defaultValue as string);
  useEffect(() => {
    if (onChangeProp) {
      onChangeProp(selectedOption);
    }
  }, [selectedOption, onChangeProp]);

  return (
    <Root>
      <Container width={width}>
        {options &&
          options.map((o: string) => {
            return (
              <Option
                key={o}
                selected={o == selectedOption}
                variant={variant}
                onClick={(_) => setSelectedOption(o)}
              >
                {o == selectedOption ? <CheckedCircleIcon /> : <EmptyCircleIcon />}
                <span>{o}</span>
                <input
                  {...switchProps}
                  type="radio"
                  ref={ref}
                  name={name}
                  value={o}
                  checked={o == selectedOption}
                  onChange={noop}
                />
              </Option>
            );
          })}
      </Container>
      {((error && error !== true) || keepErrorSpace) && (
        <InputError>{error !== true && error}</InputError>
      )}
    </Root>
  );
});
